import { useCallback, useEffect, useState } from "react";
import { tr } from "../i18n/tr";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppConfig } from "../hooks/useAppConfig";
import { ListSkeleton } from "../components/Skeletons";
import {
  GraduationCap, Loader2, ArrowRight, Calendar, Clock, MapPin, BookOpen,
  CheckCircle2, PlayCircle, Trophy, Sparkles, Search,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  inscrit:       { label: tr("lms.inscrit_f7a0", "Inscrit"),     cls: "bg-sky-500/15 text-sky-300" },
  en_cours:      { label: tr("lms.en_cours_7202", "En cours"),    cls: "bg-emerald-500/15 text-emerald-300" },
  termine:       { label: tr("lms.termine_ad1f", "Termine"),     cls: "bg-violet-500/15 text-violet-300" },
  acompte_recu:  { label: tr("lms.acompte_recu_421a", "Acompte recu"), cls: "bg-amber-500/15 text-amber-300" },
  devis_signe:   { label: tr("lms.devis_signe_d045", "Devis signe"), cls: "bg-amber-500/15 text-amber-300" },
};

export default function MesFormationsPage() {
  const { branding } = useAppConfig();
  const { axiosAuth, user } = useAuth();
  const navigate = useNavigate();
  const api = axiosAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/student/sessions");
      setSessions(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (sessions || []).filter((s) => {
    if (!q) return true;
    const n = (q || "").toLowerCase();
    return (s.title || "").toLowerCase().includes(n) || (s.program_title || "").toLowerCase().includes(n);
  });

  const inProgress = filtered.filter((s) => (s.progress || 0) > 0 && (s.progress || 0) < 100);
  const notStarted = filtered.filter((s) => (s.progress || 0) === 0);
  const completed  = filtered.filter((s) => (s.progress || 0) >= 100 || s.status === "termine");

  const avgProgress = sessions.length
    ? Math.round(sessions.reduce((a, s) => a + (s.progress || 0), 0) / sessions.length)
    : 0;

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(circle at 0% 0%, #1a1130 0%, #0b0811 60%)" }} data-testid="mes-formations-page">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "radial-gradient(circle at 80% 30%, var(--brand-primary, #f7c935) 0%, transparent 45%)"
        }} />
        <div className="absolute top-4 right-6 z-10" data-testid="lms-language-switcher"><LanguageSwitcher /></div>
        <div className="relative max-w-6xl mx-auto px-6 py-10 lg:py-14">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: "var(--brand-primary, #f7c935)" }}>
            {branding.logo_url ? <img src={branding.logo_url} alt={branding.app_name} className="h-6 object-contain" data-testid="stud-brand-logo" /> : <Sparkles className="w-3.5 h-3.5" />} {branding.app_name || tr("lms.espace_stagiaire_c56c", "Espace stagiaire")}
          </div>
          <h1 className="text-4xl lg:text-6xl font-black tracking-tighter leading-none text-white" data-testid="stud-welcome-title">
            {branding.welcome_title ? branding.welcome_title : <>Bonjour {user?.name?.split(" ")[0] || ""}, <span style={{ color: "var(--brand-primary, #f7c935)" }}>apprenons</span>.</>}
          </h1>
          <p className="text-slate-300 mt-3 text-sm lg:text-base max-w-xl" data-testid="stud-welcome-text">
            {branding.welcome_text || tr("lms.acces_a_vos_modules_0d68", "Acces a vos modules de formation, documents, evaluations et attestations. Votre progression est synchronisee en temps reel avec votre formateur.")}
          </p>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            <KPI icon={BookOpen} label={tr("lms.formations_6068", "Formations")} value={sessions.length} />
            <KPI icon={PlayCircle} label={tr("lms.en_cours_7202", "En cours")} value={inProgress.length} accent />
            <KPI icon={Trophy} label={tr("lms.terminees_9b7f", "Terminees")} value={completed.length} />
            <KPI icon={CheckCircle2} label={tr("lms.progression_moy_1eb3", "Progression moy.")} value={avgProgress + "%"} accent />
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tr("lms.rechercher_une_formation_bd1b", "Rechercher une formation...")}
              className="w-full pl-10 pr-3 py-2.5 bg-[#14101c] border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:border-[var(--brand-primary,#f7c935)] outline-none"
              data-testid="mes-formations-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-6"><ListSkeleton items={4} /></div>
        ) : sessions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {inProgress.length > 0 && <SectionGrid title={tr("lms.formation_s_en_cours_1c1c", "Formation(s) en cours")} items={inProgress} onOpen={(id) => navigate("/mes-formations/" + id)} tone="primary" />}
            {notStarted.length > 0 && <SectionGrid title={tr("lms.a_demarrer_7ea6", "A demarrer")} items={notStarted} onOpen={(id) => navigate("/mes-formations/" + id)} />}
            {completed.length > 0 && <SectionGrid title={tr("lms.terminees_9b7f", "Terminees")} items={completed} onOpen={(id) => navigate("/mes-formations/" + id)} tone="success" />}
          </>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent = false }) {
  return (
    <div className={"border rounded-2xl p-4 " + (accent ? "border-[var(--brand-primary,#f7c935)]/40 bg-[var(--brand-primary,#f7c935)]/5" : "border-white/10 bg-[#14101c]/60")}>
      <Icon className="w-5 h-5 mb-2" style={{ color: accent ? "var(--brand-primary, #f7c935)" : "#cbd5e1" }} />
      <div className="text-2xl font-black text-white leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-bold">{label}</div>
    </div>
  );
}

function SectionGrid({ title, items, onOpen, tone = "default" }) {
  const dot = tone === "success" ? "bg-emerald-400" : tone === "primary" ? "bg-[var(--brand-primary,#f7c935)]" : "bg-slate-400";
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <span className={"w-1.5 h-1.5 rounded-full " + dot} />
        <h2 className="text-sm uppercase tracking-wider text-slate-300 font-bold">{title}</h2>
        <div className="text-xs text-slate-500">· {items.length}</div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => <SessionCard key={s.session_id} session={s} onOpen={() => onOpen(s.session_id)} />)}
      </div>
    </div>
  );
}

function SessionCard({ session: s, onOpen }) {
  const status = STATUS_META[s.status] || { label: s.status || "-", cls: "bg-white/10 text-slate-300" };
  const pct = s.progress || 0;
  const coverUrl = s.cover_image || null;
  return (
    <button
      onClick={onOpen}
      data-testid={"student-sess-" + s.session_id}
      className="group text-left bg-[#14101c] border border-white/10 rounded-2xl overflow-hidden hover:border-[var(--brand-primary,#f7c935)]/40 hover:-translate-y-1 transition-all shadow-lg hover:shadow-2xl flex flex-col"
    >
      <div className="relative h-36 overflow-hidden" style={{
        background: coverUrl
          ? `url(${coverUrl}) center/cover no-repeat`
          : "linear-gradient(135deg, var(--brand-primary, #f7c935) 0%, var(--brand-secondary, #461d8a) 100%)"
      }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className={"absolute top-3 right-3 text-[9px] px-2 py-1 rounded-full font-bold uppercase " + status.cls}>{status.label}</span>
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-[10px] uppercase tracking-wider text-white/80 font-bold mb-1 line-clamp-1">{s.program_title}</div>
          <h3 className="font-black text-white text-lg leading-tight line-clamp-2">{s.title}</h3>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-1 text-[11px] text-slate-400 mb-4">
          <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {s.start_date || "-"} → {s.end_date || "-"}</div>
          {s.location && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {s.location}</div>}
          {s.modality && <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {s.modality}</div>}
        </div>

        <div className="mt-auto pt-3 border-t border-white/5">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1.5">
            <span className="uppercase tracking-wider font-bold">{tr("lms.progression_b97d", "Progression")}</span>
            <span className="text-white font-black text-sm">{pct}%</span>
          </div>
          <div className="bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{
              width: pct + "%",
              background: pct >= 100 ? "#10b981" : "linear-gradient(90deg, var(--brand-primary, #f7c935), var(--brand-secondary, #c084fc))"
            }} />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="text-slate-500 font-semibold group-hover:text-[var(--brand-primary,#f7c935)] transition-colors">{tr("lms.ouvrir_le_portail_ac0e", "Ouvrir le portail")}</span>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[var(--brand-primary,#f7c935)] group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl bg-[#14101c]/40">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 border border-white/10 bg-white/5">
        <GraduationCap className="w-7 h-7" style={{ color: "var(--brand-primary, #f7c935)" }} />
      </div>
      <div className="font-bold text-white text-lg mb-1">{tr("lms.aucune_formation_pour_l_02fa", "Aucune formation pour l'instant")}</div>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{tr("lms.vos_inscriptions_a_des_2d56", "Vos inscriptions a des sessions apparaitront ici une fois que le devis sera signe ou que l'organisme aura confirme votre place.")}</p>
    </div>
  );
}
